import { FX_FALLBACK, fmtHKD, fmtJpyAsHkdK, fmtSpreadPct, usdToHkdDisplay } from '../lib/format.ts';
import type { CardArb, CardSummary, FxRate } from '../lib/types.ts';
import { CardArt } from './CardArt.tsx';
import type { SortKey } from './Chrome.tsx';
import { Delta, SignalPill, Sparkline } from './Indicators.tsx';

interface CardTileProps {
  card: CardSummary;
  history?: number[]; // optional 48-day sparkline. Detail-light list omits this.
  selected?: boolean;
  onClick?: () => void;
  dense?: boolean;
  /** Display FX (rate + coupon). Defaults to the fallback in format.ts. */
  fx?: FxRate;
  /** Active dashboard sort. When sorting by 30d the tile shows 30d change
   *  instead of 24h, so the user sees the field they're sorting on. */
  sort?: SortKey;
}

export function CardTile({
  card,
  history,
  selected = false,
  onClick,
  dense = false,
  fx = FX_FALLBACK,
  sort,
}: CardTileProps) {
  const sparkColor =
    (card.d7 ?? 0) > 0.05 ? '#5fe3a1' : (card.d7 ?? 0) < -0.05 ? '#ff6b6b' : '#8a8d93';
  const hkd = card.price == null ? null : usdToHkdDisplay(card.price, fx);
  const arb = card.arb ?? null;
  const actionable = arb?.actionable ?? false;
  return (
    <button
      type="button"
      className={`tile sig-tile-${card.signal} ${selected ? 'is-selected' : ''} ${card.owned ? 'is-owned' : ''} ${actionable ? 'is-arb-actionable' : ''}`}
      onClick={onClick}
      aria-label={card.owned ? `${card.jpEn ?? card.jp} (held)` : undefined}
    >
      <div className="slab-head">
        <span className="grade">
          PSA <span className="ten">10</span>
        </span>
        <span className="setcode">
          {card.set} · {card.num}
        </span>
        <SignalPill signal={card.signal} score={card.score} />
        {(card.burstCount ?? 0) >= 5 && (
          <span className="burst-badge" title={`${card.burstCount} sales in 5 min in last 48h`}>
            🔥 {card.burstCount}
          </span>
        )}
      </div>
      <div className="tile-art-wrap">
        <CardArt card={card} />
      </div>
      <div className="tile-foot">
        <div className={card.jpEn ? 'tile-name' : 'tile-name jp'}>{card.jpEn ?? card.jp}</div>
        <div className="tile-price-row">
          <span className="tile-price mono">{hkd == null ? '—' : `HK$${fmtHKD(hkd)}`}</span>
          {/* Show 30d delta when the user is sorting by 30d so the field they're
              ranking on is visible on the tile. Otherwise show 24h. For the
              24h case, treat "no data today" as 0% (operator preference) —
              for 30d, null stays as "—" since it means we have no 30d span yet. */}
          {sort === 'd30-desc' || sort === 'd30-asc' ? (
            <span className="tile-delta-wrap">
              <span className="tile-delta-prefix">30d</span>
              <Delta v={card.d30} />
            </span>
          ) : (
            <Delta v={card.d1 ?? 0} />
          )}
        </div>
        {/* Arb row REPLACES the sparkline for arb cards (same 18px slot, tile
            height unchanged). Non-arb cards keep the sparkline as today. In
            dense view we keep things quiet — only the actionable arb earns
            pixels (it's the whole point). */}
        {arb ? (
          dense ? (
            actionable ? <ArbTileRow arb={arb} fx={fx} /> : null
          ) : (
            <ArbTileRow arb={arb} fx={fx} />
          )
        ) : (
          !dense &&
          history &&
          history.length > 1 && (
            <Sparkline data={history} color={sparkColor} width={140} height={16} />
          )
        )}
      </div>
    </button>
  );
}

function ArbTileRow({ arb, fx }: { arb: CardArb; fx: FxRate }) {
  // If the dropship side has clear positive edge and the import side does
  // NOT (no actionable + import spread negative or zero), surface the → US
  // pill instead — that's the only direction with money on the table for
  // this card. Hoopa is the canonical example: -$165 → JP, +$913 → US.
  const dropPositive = arb.dropship && arb.dropship.netSpreadJpy > 0;
  const importPositive = arb.actionable || (arb.netSpreadJpy ?? 0) > 0;
  if (dropPositive && !importPositive && arb.dropship) {
    const d = arb.dropship;
    return (
      <div className="tile-arb is-actionable">
        <span className="arb-mark">◆</span>
        <span className="arb-label">→ US</span>
        <span className="arb-pct">{fmtSpreadPct(d.netSpreadPct)}</span>
        <span className="arb-abs">{fmtJpyAsHkdK(d.netSpreadJpy, fx)}</span>
      </div>
    );
  }
  // Label clarifies the trade direction. The current spread is the IMPORT
  // thesis (buy eBay → sell Snkrdunk, yen lands in JP), hence "→ JP".
  if (arb.listing && arb.actionable) {
    return (
      <div className="tile-arb is-actionable">
        <span className="arb-mark">◆</span>
        <span className="arb-label">→ JP</span>
        <span className="arb-pct">{fmtSpreadPct(arb.netSpreadPct ?? 0)}</span>
        <span className="arb-abs">{fmtJpyAsHkdK(arb.netSpreadJpy ?? 0, fx)}</span>
      </div>
    );
  }
  if (arb.listing || arb.netSpreadJpy != null) {
    return (
      <div className="tile-arb is-dim">
        <span className="arb-mark">◇</span>
        <span className="arb-label">→ JP</span>
        <span className="arb-pct">{fmtSpreadPct(arb.netSpreadPct ?? 0)}</span>
        <span className="arb-note" style={{ marginLeft: 'auto' }}>
          no edge
        </span>
      </div>
    );
  }
  return (
    <div className="tile-arb is-scan">
      <span className="arb-mark">◇</span>
      <span className="arb-note">→ JP scanned · no match</span>
      <span className="arb-cadence">{arb.scanCadence}</span>
    </div>
  );
}
